import { Component, ViewChild } from '@angular/core';
import {
  ViewWillEnter,
  ViewWillLeave,
  IonContent,
  Platform,
  ActionSheetController,
  ModalController,
  PopoverController,
} from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { ServiceRequestAnalyticsService } from '@usf/ngrx-list';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import {
  PHOTO_OPTIONS,
  FILE_OPTIONS,
} from '@shared/services/file/model/file.model';
import { ModalService } from '@shared/services/modal/modal.service';
import { ServiceRequestService } from '@service-request/services/service-request.service';
import { IssueAndIssueDetailsDesktopComponent } from './components/issue-and-issue-details/issue-and-issue-details-desktop/issue-and-issue-details-desktop.component';
import { IssueDetailsOptionsTouchComponent } from './components/issue-and-issue-details/issue-and-issue-details-touch/issue-details-options-touch/issue-details-options-touch.component';
import { IssueOptionsTouchComponent } from './components/issue-and-issue-details/issue-and-issue-details-touch/issue-options-touch/issue-options-touch.component';
import { OrderAndProductOptionsDesktopComponent } from './components/order-and-product-options/order-and-product-options-desktop/order-and-product-options-desktop.component';
import { OrderOptionsTouchComponent } from './components/order-and-product-options/order-and-product-options-touch/order-options-touch/order-options-touch.component';
import { ProductOptionsTouchComponent } from './components/order-and-product-options/order-and-product-options-touch/product-options-touch/product-options-touch.component';
import { RequestCreditData } from './models/request-credit.model';
import { FEATURES } from '@shared/constants/splitio-features';

@Component({
  selector: 'app-request-credit',
  templateUrl: './request-credit.page.html',
  styleUrls: ['./request-credit.page.scss'],
})
export class RequestCreditPage implements ViewWillEnter, ViewWillLeave {
  @ViewChild(IonContent) content: IonContent;
  @ViewChild('emailInfoPopover') popover;
  breadcrumbs: Crumb[];
  acceptedPhotoTypes = PHOTO_OPTIONS;
  acceptedFileTypes = FILE_OPTIONS;
  submitButtonDisabled = true;
  openPopover = false;
  timeoutFunc;
  showEmailField = false;

  //Feature flags
  divisionDfgBannerFlag = FEATURES.split_division_dfg_banner.name;

  constructor(
    readonly platform: Platform,
    private actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private popoverController: PopoverController,
    readonly platformService: PlatformService,
    private modalService: ModalService,
    readonly serviceRequestService: ServiceRequestService,
    private serviceRequestAnalyticsService: ServiceRequestAnalyticsService,
    public panAppState: PanAppState,
  ) {}

  async ionViewWillEnter(): Promise<void> {
    this.panAppState.setPageTitle('Request Credit');
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.credit',
        url: '',
        active: true,
      },
    ];
    this.serviceRequestAnalyticsService.trackRequestCreditPageLoad();
    this.content.scrollToTop();
    this.serviceRequestService.isPickUp = false;
    await this.serviceRequestService.populateUserEmail();
  }

  async openOrderOptionsModal() {
    if (
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop
    ) {
      this.modalPrep();
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: OrderOptionsTouchComponent,
      });
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );
      modal.onDidDismiss().then(this.handleOnDismiss);
      await modal.present();
    } else {
      this.openOrderAndProductOptionsModalDesktop();
    }
  }

  async openProductOptionsModal() {
    if (
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop
    ) {
      this.modalPrep();
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: ProductOptionsTouchComponent,
      });
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );
      modal.onDidDismiss().then(this.handleOnDismiss);
      await modal.present();
    } else {
      this.openOrderAndProductOptionsModalDesktop();
    }
  }

  async openOrderAndProductOptionsModalDesktop() {
    this.modalPrep();
    const modal = await this.modalController.create({
      component: ModalWithNavComponent,
      componentProps: {
        rootPage: OrderAndProductOptionsDesktopComponent,
      },
      cssClass: 'modal-desktop-size-xl',
      mode: 'ios',
    });

    modal.onDidDismiss().then(this.handleOnDismiss);
    await modal.present();
  }

  getOrderTypeLabel(orderType: string): string {
    return orderType ? 'i18n.requestCredit.orderType' + orderType : '-';
  }

  async openIssueOptionsModal() {
    if (
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop
    ) {
      this.modalPrep();
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: IssueOptionsTouchComponent,
      });
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );
      modal.onDidDismiss().then(this.handleOnDismiss);
      await modal.present();
    } else {
      this.openIssueAndIssueDetailsOptionsModalDesktop();
    }
  }

  async openIssueDetailsOptionsModal() {
    if (
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop
    ) {
      this.modalPrep();
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: IssueDetailsOptionsTouchComponent,
      });
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );
      modal.onDidDismiss().then(this.handleOnDismiss);
      await modal.present();
    } else {
      this.openIssueAndIssueDetailsOptionsModalDesktop();
    }
  }

  async openIssueAndIssueDetailsOptionsModalDesktop() {
    this.modalPrep();
    const modal = await this.modalController.create({
      component: ModalWithNavComponent,
      componentProps: {
        rootPage: IssueAndIssueDetailsDesktopComponent,
      },
      cssClass: 'modal-desktop-size-xl',
      mode: 'ios',
    });

    modal.onDidDismiss().then(this.handleOnDismiss);
    await modal.present();
  }

  async openQuantityOptions(event: any) {
    const buttons = [];
    buttons.push(
      {
        text: 'CS',
        handler: () => {
          this.serviceRequestService.setQuantityUnits('CS');
          this.dismissQuantityOptions();
        },
      },
      {
        text: 'EA',
        handler: () => {
          this.serviceRequestService.setQuantityUnits('EA');
          this.dismissQuantityOptions();
        },
      },
    );

    if (!this.platformService.isTouch.value) {
      const quantityPopover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        event,
        componentProps: {
          buttons,
        },
        cssClass: 'quantity-units-popover-desktop',
        mode: 'ios',
        showBackdrop: false,
      });
      await quantityPopover.present();
    } else {
      buttons.push({
        text: 'Cancel',
        role: 'cancel',
        cssClass: 'cancel-button',
      });
      const quatityActionSheet = await this.actionSheetController.create({
        mode: 'ios',
        cssClass: 'action-sheet-touch',
        buttons: buttons,
      });
      await quatityActionSheet.present();
    }
  }

  async dismissQuantityOptions() {
    if (!this.platformService.isTouch.value) {
      await this.popoverController.dismiss();
    }
  }

  incrementQuantity() {
    if (this.serviceRequestService.selectedParams.quantity < 999) {
      this.serviceRequestService.setQuantity(
        Number(this.serviceRequestService.selectedParams.quantity) + 1,
      );
    } else if (
      this.serviceRequestService.selectedParams.quantity === undefined
    ) {
      this.serviceRequestService.setQuantity(1);
    }
  }

  decrementQuantity() {
    if (this.serviceRequestService.selectedParams.quantity > 0) {
      this.serviceRequestService.setQuantity(
        this.serviceRequestService.selectedParams.quantity === 1
          ? undefined
          : Number(this.serviceRequestService.selectedParams.quantity) - 1,
      );
    }
  }

  setEmail(event: any) {
    this.serviceRequestService.setEmail(event?.target?.value);
    this.serviceRequestService.setSubmitButtonReady();
  }

  setShowRequiredProductReceivedInputLogic() {
    this.serviceRequestService.showRequiredProductReceivedInput.next(
      !this.serviceRequestService.selectedParams.productReceived ? true : false,
    );
    this.serviceRequestService.setSubmitButtonReady();
  }

  setShowRequiredCommentsInputLogic() {
    this.serviceRequestService.showRequiredCommentsInput.next(
      this.serviceRequestService?.commentsRequired &&
        !this.serviceRequestService.selectedParams.comments
        ? true
        : false,
    );
    this.serviceRequestService.setSubmitButtonReady();
  }

  closePopover = async () => {
    if (this.popoverController.getTop()) {
      await this.popoverController.dismiss();
    }
  };

  shouldCloseAcceptedFileTypesPopover(event: any) {
    window.addEventListener('blur', this.closePopover, { once: true });
  }

  removeWindowListener = () => {
    window.removeEventListener('blur', this.closePopover);
  };

  modalPrep() {
    this.serviceRequestService.prepareForModal();
  }

  handleOnDismiss = (data: { data: any; role: string }) => {
    if (
      data.data ||
      (data.role !== undefined &&
        (data.role === 'gesture' || data.role === 'backdrop'))
    ) {
      this.serviceRequestService.handleReset();
    } else {
      this.serviceRequestService.finalizeParams();
    }
    this.serviceRequestService.searchKey = '';
  };

  handleScroll() {
    if (
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop
    ) {
      this.content.scrollToPoint(0, 500, 0);
    }
  }

  handleReSealable(selection: boolean) {
    this.serviceRequestService.setReSeal(selection);
  }

  onSubmit() {
    if (!this.serviceRequestService.verifyRequest()) {
      return;
    }
    const trackingData = this.mapRequestCreditDataForAnalytics(
      this.serviceRequestService.selectedParams,
    );
    this.serviceRequestService.setTrackingData(trackingData);
    this.serviceRequestService.handleRequestConfirmation(trackingData);
  }

  mapRequestCreditDataForAnalytics(selectedParams: RequestCreditData) {
    let quantityUnits: string;
    let quantity: number;
    let productData = [];
    let orderItem = selectedParams.order.orderItems.find(
      item => item.productNumber === selectedParams.productNumber,
    );
    const productCommonData: any = {
      divisionApn:
        selectedParams.order.orderHeader.divisionNumber +
        '-' +
        orderItem.productNumber,
      attributes: selectedParams.product.trackingAttributes,
    };
    quantityUnits = selectedParams.quantityUnits;
    quantity = selectedParams.quantity;
    let productDataItem = {
      ...productCommonData,
      quantityUnits,
      quantity,
    };
    productData.push(productDataItem);

    const fileExtensionSplitted = selectedParams?.file?.extension.split('.');
    const fileType = fileExtensionSplitted
      ? '.' + fileExtensionSplitted[fileExtensionSplitted.length - 1]
      : null;
    const invalidFileType = fileType
      ? this.acceptedPhotoTypes.includes(fileType)
      : false;
    const trackingData = {
      orderId: selectedParams.order.orderHeader.orderId,
      hasPhoto:
        selectedParams?.photos === undefined ||
        selectedParams?.photos.length === 0
          ? false
          : true,
      hasDocument:
        selectedParams?.file === undefined || invalidFileType ? false : true,
      productData,
    };

    return trackingData;
  }

  popoverInteraction = (event: any, interact: boolean) => {
    if (event !== undefined) {
      event.stopPropagation();
      this.popover.event = event;
      this.timeoutFunc = setTimeout(() => {
        this.openPopover = false;
      }, 5000);
    }
    this.openPopover = interact;
  };

  // The binding wants an event, but event does not macth the returned data from the modal
  handlePickUpDismissBySlideDown = (event: any) => {
    const role = event?.detail?.role;
    if (role === 'gesture' || role === 'backdrop') {
      this.serviceRequestService.handlePickupQuestionDismiss(false);
    }
  };

  handlePickupQuestionDismiss = (value: boolean) => {
    this.serviceRequestService.handlePickupQuestionDismiss(value);
  };

  ionViewWillLeave(): void {
    this.serviceRequestService.requestParamsReset();
  }
}
